import { useState, useEffect } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Calendar, Clock, Users, Palette } from "lucide-react";

export const PlanningPage = () => {
  const [planning, setPlanning] = useState([]);
  const [loading, setLoading] = useState(true);
  // false = couleur uniforme (bleue) ; true = couleur du type de commande
  const [colorByType, setColorByType] = useState(() => {
    try { return localStorage.getItem("planning_color_by_type") === "1"; } catch { return false; }
  });

  const toggleColorByType = (v) => {
    setColorByType(v);
    try { localStorage.setItem("planning_color_by_type", v ? "1" : "0"); } catch { /* ignore */ }
  };

  const fetchPlanning = async () => {
    try {
      setLoading(true);
      const response = await api.getPlanning();
      setPlanning(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du planning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanning();
  }, []);

  // Couleur par défaut (bleu marque) — utilisée quand colorByType=false ou type sans couleur
  const DEFAULT_BAR_COLOR = "#1d4ed8";
  const DEFAULT_TEXT_COLOR = "#ffffff";

  const barStyle = (item) => {
    if (colorByType && item.color) {
      return { backgroundColor: item.color, color: item.text_color || "#ffffff" };
    }
    return { backgroundColor: DEFAULT_BAR_COLOR, color: DEFAULT_TEXT_COLOR };
  };

  // Calculate total days
  const totalDays = planning.reduce((sum, item) => sum + item.duration_days, 0);

  return (
    <div className="space-y-6 animate-in" data-testid="planning-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Planning</h1>
          <p className="text-muted-foreground mt-1">
            Planning estimatif des chantiers à réaliser
          </p>
        </div>
        <div className="flex items-center gap-3 bg-zinc-50 border rounded-md px-3 py-2" data-testid="planning-color-toggle">
          <Palette className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="color-by-type" className="text-sm cursor-pointer">
            Couleur par type de commande
          </Label>
          <Switch
            id="color-by-type"
            checked={colorByType}
            onCheckedChange={toggleColorByType}
            data-testid="planning-color-by-type-switch"
          />
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-primary/10 rounded-sm flex items-center justify-center">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Commandes planifiées</p>
                <p className="text-2xl font-heading font-bold">{planning.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-yellow-100 rounded-sm flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Durée totale estimée</p>
                <p className="text-2xl font-heading font-bold">{totalDays.toFixed(1)} jours</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-green-100 rounded-sm flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">CA en stock</p>
                <p className="text-2xl font-heading font-bold currency">
                  {formatCurrency(planning.reduce((sum, item) => sum + item.amount_ht, 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gantt-like view */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Timeline des chantiers</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : planning.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune commande en attente ou en cours</p>
            </div>
          ) : (
            <div className="space-y-4">
              {planning.map((item, index) => (
                <div
                  key={item.order_id}
                  className="relative flex items-center gap-4 p-4 bg-muted/30 rounded-sm animate-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`planning-item-${item.order_id}`}
                >
                  {/* Info */}
                  <div className="w-48 shrink-0">
                    <p className="font-mono text-sm text-muted-foreground">{item.reference}</p>
                    <p className="font-medium truncate">{item.client_name}</p>
                    <p className="text-sm text-muted-foreground currency">
                      {formatCurrency(item.amount_ht)}
                    </p>
                  </div>

                  {/* Gantt bar */}
                  <div className="flex-1 h-12 bg-gray-100 rounded-sm overflow-hidden relative">
                    <div
                      className="gantt-bar absolute left-0 top-0 bottom-0"
                      style={{
                        width: `${Math.min(100, (item.duration_days / totalDays) * 100 * 3)}%`,
                        minWidth: "80px",
                        ...barStyle(item),
                      }}
                      data-testid={`gantt-bar-${item.order_id}`}
                      title={item.order_type_name || ""}
                    >
                      {item.duration_days.toFixed(1)} jours
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="w-32 shrink-0 text-right">
                    <Badge variant="outline" className="mb-1">
                      {item.executants} exécutant{item.executants > 1 ? "s" : ""}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      {item.start_date} → {item.end_date}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed list */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Détail des commandes</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {planning.length > 0 && (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Client</th>
                    <th>Montant HT</th>
                    <th>Exécutants</th>
                    <th>Durée estimée</th>
                    <th>Début prévu</th>
                    <th>Fin prévue</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {planning.map((item) => (
                    <tr key={item.order_id}>
                      <td className="font-mono text-sm">{item.reference}</td>
                      <td className="font-medium">{item.client_name}</td>
                      <td className="currency">{formatCurrency(item.amount_ht)}</td>
                      <td>{item.executants}</td>
                      <td>{item.duration_days.toFixed(1)} jours</td>
                      <td>{item.start_date}</td>
                      <td>{item.end_date}</td>
                      <td>
                        <Badge className={item.status === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-blue-100 text-blue-800"}>
                          {item.status === "pending" ? "En attente" : "En cours"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlanningPage;
